import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationPopup from './NotificationPopup';
import {
  LayoutDashboard,
  Users,
  Building2,
  FolderKanban,
  Clock,
  CheckSquare,
  Upload,
  BarChart3,
  Settings,
  LogOut,
  X,
  Menu,
} from 'lucide-react';

const NavItem = ({ to, icon: Icon, label, end }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
        isActive
          ? 'bg-white/20 text-white'
          : 'text-blue-100 hover:bg-white/10 hover:text-white'
      }`
    }
  >
    <Icon size={17} strokeWidth={1.75} />
    <span>{label}</span>
  </NavLink>
);

const Layout = ({ children }) => {
  const { user, logout, isStaff, isAdminMaster, isGestor, isConsultor } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard';
    if (path === '/users') return 'Usuários';
    if (path === '/users/new') return 'Novo Usuário';
    if (path.includes('/users/') && path.includes('/edit')) return 'Editar Usuário';
    if (path === '/clients') return 'Clientes';
    if (path === '/clients/new') return 'Novo Cliente';
    if (path.includes('/clients/') && path.includes('/edit')) return 'Editar Cliente';
    if (path === '/projects') return 'Projetos';
    if (path === '/projects/new') return 'Novo Projeto';
    if (path.includes('/projects/') && path.includes('/edit')) return 'Editar Projeto';
    if (path === '/time-entries') return isStaff ? 'Lançamentos' : 'Meus Lançamentos';
    if (path === '/time-entries/new') return 'Novo Lançamento';
    if (path.includes('/time-entries/') && path.includes('/edit')) return 'Editar Lançamento';
    if (path.includes('/time-entries/') && path.includes('/copy')) return 'Copiar Lançamento';
    if (path === '/approvals') return 'Aprovação de Horas';
    if (path === '/imports') return 'Importar Dados';
    if (path === '/reports') return 'Relatórios';
    if (path === '/settings') return 'Configurações';
    return 'Gestão de Consultores';
  };

  const getRoleLabel = () => {
    if (isAdminMaster) return 'Administrador Master';
    if (isGestor) return 'Gestor';
    if (isConsultor) return 'Consultor';
    return 'Usuário';
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-30 w-64 flex flex-col transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: '#1e3a5f' }}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div>
            <h1 className="text-white text-xl font-bold tracking-tight">Kronex</h1>
            <p className="text-blue-200 text-xs mt-0.5">Gestão de Consultores</p>
          </div>
          <button
            className="lg:hidden text-white/60 hover:text-white transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={18} />
          </button>
        </div>

        {/* User info */}
        <div className="px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-white font-semibold text-sm">
              {user?.username?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.username}</p>
              <p className="text-blue-200 text-xs truncate">{getRoleLabel()}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" end />

          {isStaff ? (
            <>
              <NavItem to="/users" icon={Users} label="Usuários" />
              <NavItem to="/clients" icon={Building2} label="Clientes" />
              <NavItem to="/projects" icon={FolderKanban} label="Projetos" />
              <NavItem to="/approvals" icon={CheckSquare} label="Aprovação de Horas" />
              <NavItem to="/imports" icon={Upload} label="Importar Dados" />
              <NavItem to="/time-entries" icon={Clock} label="Lançamentos" />
              <NavItem to="/reports" icon={BarChart3} label="Relatórios" />
              <NavItem to="/settings" icon={Settings} label="Configurações" />
            </>
          ) : (
            <>
              <NavItem to="/time-entries" icon={Clock} label="Meus Lançamentos" />
              <NavItem to="/reports" icon={BarChart3} label="Meus Relatórios" />
            </>
          )}
        </nav>

        {/* Logout */}
        <div className="px-3 py-4 border-t border-white/10">
          <button
            onClick={logout}
            className="flex items-center gap-3 w-full px-4 py-2.5 rounded-lg text-sm font-medium text-blue-100 hover:bg-white/10 hover:text-white transition-all duration-150"
          >
            <LogOut size={17} strokeWidth={1.75} />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4 shrink-0">
          <button
            className="lg:hidden text-gray-500 hover:text-gray-700"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={22} />
          </button>
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{getPageTitle()}</h2>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>

      {/* Notificações exibidas ao logar (avisos do gestor/master) */}
      <NotificationPopup />
    </div>
  );
};

export default Layout;
